import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast'; // Import Toaster
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useWebsiteConfig } from './hooks/useWebsiteConfig';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UsersPage from './components/UsersPage';
import DonationsPage from './components/DonationsPage';
import PoojasPage from './components/PoojasPage'; // eslint-disable-line @typescript-eslint/no-unused-vars
import PoojaBookingsPage from './components/PoojaBookingsPage';
import TempleDashboard from './components/TempleDashboard';
import TempleRegistrationPage from './components/TempleRegistrationPage';
import PriestManagementPage from './components/PriestManagementPage';
import AdminManagementPage from './components/AdminManagementPage';
import ReportsPage from './components/ReportsPage';
import ProfilePage from './components/ProfilePage';
import WebsiteConfigPage from './components/WebsiteConfigPage';
import TempleDonationsPage from './components/TempleDonationsPage';
import TempleReportsPage from './components/TempleReportsPage';
import EventsPage from './components/EventsPage';
import EventBookingsPage from './components/EventBookingsPage';
import SponsorshipsPage from './components/SponsorshipsPage';
import SponsorshipBookingsPage from './components/SponsorshipBookingsPage';
import SubscriptionPlansPage from './components/SubscriptionPlansPage';
import ApiSubscriptionPlansPage from './components/ApiSubscriptionPlansPage';
import DonationCausesPage from './components/DonationCausesPage';
import HeroBannersPage from './components/HeroBannersPage';
import PagesPage from './components/PagesPage';
import DynamicFavicon from './components/DynamicFavicon';
import { Header } from './components/common/Header';
import { Sidebar } from './components/common/Sidebar';
import DevoteesPage from './components/DevoteesPage';
import TempleAdminsPage from './components/TempleAdminsPage';
import LegacyDataImportPage from './components/LegacyDataImportPage';
import LegacyDataPage from './components/LegacyDataPage';
import LegacyDevoteesPage from './components/LegacyDevoteesPage';
import LegacyTransactionsPage from './components/LegacyTransactionsPage';
import { SankalpamPage } from './components/SankalpamPage';
import TicketsPage from './components/TicketsPage';

const DEFAULT_THEME = {
  primaryColor: '#F7941D',
  secondaryColor: '#9C6D3F',
  tertiaryColor: '#E36511',
  neutralColor: '#FDF7F2',
  fontFamily: 'Inter',
  tableHeaderColor: '#FFF7ED',
  sidebarBgColor: '#FFFFFF',
} as const;

type AdminTheme = {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  neutralColor: string;
  fontFamily: string;
  tableHeaderColor?: string;
  sidebarBgColor?: string;
};

const FONT_STACKS: Record<string, string> = {
  Inter: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  Poppins: 'Poppins, Inter, system-ui, sans-serif',
  Roboto: 'Roboto, Inter, system-ui, sans-serif',
  'Open Sans': '"Open Sans", Inter, system-ui, sans-serif',
  Lato: 'Lato, Inter, system-ui, sans-serif',
  'Playfair Display': '"Playfair Display", Georgia, serif',
};

const THEME_CACHE_KEY = 'fd_admin_theme_preview';

function applyThemeToAdminRoot(themeInput: Partial<AdminTheme> = {}) {
  if (typeof document === 'undefined') return;
  const t = { ...DEFAULT_THEME, ...themeInput };
  const root = document.documentElement;
  root.style.setProperty('--color-primary', t.primaryColor);
  root.style.setProperty('--color-secondary', t.secondaryColor);
  root.style.setProperty('--color-tertiary', t.tertiaryColor);
  root.style.setProperty('--color-neutral', t.neutralColor);
  root.style.setProperty('--table-header-bg', t.tableHeaderColor || '#FFF7ED');
  root.style.setProperty('--sidebar-bg', t.sidebarBgColor || '#FFFFFF');
  root.style.setProperty('--font-family', FONT_STACKS[t.fontFamily] || `${t.fontFamily}, sans-serif`);
}

function loadGoogleFontIfNeeded(fontFamily: string) {
  if (typeof document === 'undefined') return;
  const supported = ['Inter', 'Poppins', 'Roboto', 'Open Sans', 'Lato', 'Playfair Display'];
  if (!supported.includes(fontFamily)) return;
  const id = `admin-runtime-font-${fontFamily.toLowerCase().replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function AppContent() {
  // Initialize state from URL param to persist on refresh
  const [activeTab, setActiveTabState] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('tab') || 'dashboard';
    }
    return 'dashboard';
  });
  const [selectedLegacyDevotee, setSelectedLegacyDevotee] = useState<any>(null);

  const setActiveTab = (tabId: string) => {
    setActiveTabState(tabId);
    setSelectedLegacyDevotee(null);
    // Update URL without reload
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('tab', tabId);
    window.history.pushState({}, '', newUrl.toString());
  };

  // Listen for back/forward browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab') || 'dashboard';
      setActiveTabState(tab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { config } = useWebsiteConfig();

  useEffect(() => {
    // Fast initial apply from cached admin preview
    try {
      const cached = localStorage.getItem(THEME_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached || '{}');
        applyThemeToAdminRoot({
          primaryColor: parsed.primaryColor,
          secondaryColor: parsed.secondaryColor,
          tertiaryColor: parsed.tertiaryColor || parsed.accentColor,
          neutralColor: parsed.neutralColor,
          fontFamily: parsed.fontFamily,
          tableHeaderColor: parsed.tableHeaderColor,
          sidebarBgColor: parsed.sidebarBgColor,
        });
        if (parsed.fontFamily) loadGoogleFontIfNeeded(parsed.fontFamily);
      } else {
        applyThemeToAdminRoot(DEFAULT_THEME);
      }
    } catch {
      applyThemeToAdminRoot(DEFAULT_THEME);
    }
  }, []);

  useEffect(() => {
    const theme = config?.settings?.theme;
    if (!theme) return;
    const normalized = {
      primaryColor: theme.primaryColor || DEFAULT_THEME.primaryColor,
      secondaryColor: theme.secondaryColor || DEFAULT_THEME.secondaryColor,
      tertiaryColor: theme.tertiaryColor || theme.accentColor || DEFAULT_THEME.tertiaryColor,
      neutralColor: theme.neutralColor || DEFAULT_THEME.neutralColor,
      fontFamily: theme.fontFamily || DEFAULT_THEME.fontFamily,
      tableHeaderColor: theme.tableHeaderColor,
      sidebarBgColor: theme.sidebarBgColor,
    };
    applyThemeToAdminRoot(normalized);
    loadGoogleFontIfNeeded(normalized.fontFamily);
  }, [config?.settings?.theme]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveTab} />;
      case 'profile':
        return <ProfilePage />;
      case 'temple-dashboard':
        return <TempleDashboard />;

      case 'temple-registrations':
        return <TempleRegistrationPage />;
      case 'users':
        return <UsersPage />;
      case 'users-devotees':
        return <DevoteesPage />;
      case 'users-priests':
        return <PriestManagementPage />;
      case 'users-temple-admins':
        return <TempleAdminsPage />;
      case 'admins':
        return <AdminManagementPage />;
      case 'donations':
        return <DonationsPage />;
      case 'donation-causes':
        return <DonationCausesPage />;
      case 'poojas':
        return <PoojasPage />;
      case 'pooja-bookings':
        return <PoojaBookingsPage />;
      case 'events':
        return <EventsPage />;
      case 'event-bookings':
        return <EventBookingsPage />;
      case 'sponsorships':
        return <SponsorshipsPage />;
      case 'sponsorship-bookings':
        return <SponsorshipBookingsPage />;
      case 'temple-donations':
        return <TempleDonationsPage />;
      case 'temple-reports':
        return <TempleReportsPage />;
      case 'priests':
        return <PriestManagementPage />;
      case 'reports':
        return <ReportsPage />;
      case 'website-config':
        return <WebsiteConfigPage />;
      case 'hero-banners':
        return <HeroBannersPage />;
      case 'pages':
        return <PagesPage />;
      case 'subscription-plans':
        return <SubscriptionPlansPage />;
      case 'api-subscription-plans':
        return <ApiSubscriptionPlansPage />;
      case 'sankalpam':
        return <SankalpamPage />;
      case 'legacy-data-import':
        return <LegacyDataImportPage />;
      case 'legacy-data':
        return <LegacyDataPage />;
      case 'legacy-devotees':
        return selectedLegacyDevotee ? (
          <LegacyTransactionsPage
            devotee={selectedLegacyDevotee}
            onBack={() => setSelectedLegacyDevotee(null)}
          />
        ) : (
          <LegacyDevoteesPage
            onViewTransactions={(d) => setSelectedLegacyDevotee(d)}
          />
        );
      case 'tickets':
        return <TicketsPage />;
      default:
        return <Dashboard onNavigate={setActiveTab} />;
    }
  };

  return (
    <div className="admin-panel">
      {/* Header */}
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        <Header
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          user={user}
          logout={logout}
          setActiveTab={setActiveTab}
          config={config}
        />

        {/* Main Content */}
        <main className="flex-1 p-0 overflow-x-hidden">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();

  return (
    <>
      <DynamicFavicon />
      {loading ? (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#6b7280' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
      ) : !user ? (
        <Login />
      ) : (
        <AppContent />
      )}
    </>
  );
}

function AppWithProvider() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <App />
    </AuthProvider>
  );
}

export default AppWithProvider;